import { useCallback, useEffect, useState } from 'react';
import './CollapsibleSection.css';

// ============================================================
// A section with a header that opens and closes the body.
//
//   <CollapsibleSection id="homes" title="Homes" summary={…} actions={<button/>}>
//
// - The whole header row toggles (a real <button> carries aria-expanded and
//   Enter/Space). Buttons in `actions` never toggle: their clicks stop at the
//   actions wrapper.
// - Open/closed is remembered per id in localStorage "shop.collapse.<id>"
//   ('1' open, '0' closed). Missing, unreadable or blocked storage = defaultOpen.
// - Height animates (CSS grid 0fr → 1fr); prefers-reduced-motion turns it off.
//   The body stays mounted when closed (its state survives) but is inert.
// - Anything can open it from outside: dispatch
//   window 'shop:open-section' with detail = id (the Homes jump link does).
// ============================================================

export const storageKey = (id) => `shop.collapse.${id}`;
export const OPEN_EVENT = 'shop:open-section';

function readOpen(id, fallback) {
  try {
    const v = window.localStorage.getItem(storageKey(id));
    return v === '1' ? true : v === '0' ? false : fallback;
  } catch {
    return fallback;
  }
}
function writeOpen(id, open) {
  try { window.localStorage.setItem(storageKey(id), open ? '1' : '0'); } catch { /* storage blocked: session only */ }
}

/** Open a section by id, then (next frame) scroll it into view. */
export function openSection(id, { scroll = true } = {}) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: id }));
  if (scroll) {
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
}

export default function CollapsibleSection({ id, title, summary, actions, defaultOpen = true, className = '', children }) {
  const [open, setOpen] = useState(() => readOpen(id, defaultOpen));

  const set = useCallback((next) => {
    setOpen((cur) => {
      const v = typeof next === 'function' ? next(cur) : next;
      writeOpen(id, v);
      return v;
    });
  }, [id]);

  useEffect(() => {
    const onOpen = (e) => { if (e.detail === id) set(true); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [id, set]);

  const bodyId = `${id}-body`;
  return (
    <section id={id} className={`CS ${open ? 'CS--open' : 'CS--closed'} ${className}`.trim()} aria-label={typeof title === 'string' ? title : undefined}>
      <div className="CS-head" onClick={() => set((v) => !v)}>
        <button type="button" className="CS-toggle" aria-expanded={open} aria-controls={bodyId} data-testid={`cs-toggle-${id}`}>
          <span className="CS-title">{title}</span>
          {summary && <span className="CS-summary">{summary}</span>}
        </button>
        {actions && (
          <div className="CS-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
        <span className="CS-chevron" aria-hidden="true">▾</span>
      </div>
      <div className="CS-body" id={bodyId} inert={!open} aria-hidden={!open}>
        <div className="CS-inner">{children}</div>
      </div>
    </section>
  );
}
